import {
  Box,
  Card,
  IconButton,
  InputAdornment,
  Typography,
} from "@mui/material";
import TextField from "@mui/material/TextField";
import { Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import Button from "../../../common/components/Button";
import { Logo } from "../../../common/components/Logo";
import { toast } from "../../../common/components/Toast";
import { SPACING } from "../../../common/constants/spacing";
import { resetPasswordWithToken } from "../../api/auth";
import styles from "../Login/Login.module.css";

export default function ResetPassword() {
  const { t } = useTranslation("authentication");
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get("email");
    const tokenParam = searchParams.get("token");

    if (!emailParam || !tokenParam) {
      setError("Invalid reset link. Please request a new password reset.");
      return;
    }

    setEmail(emailParam);
    setToken(tokenParam);
  }, [searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return t("resetPassword.errors.passwordTooShort");
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const { newPassword, confirmPassword } = formData;

    if (!newPassword || !confirmPassword) {
      setError(t("resetPassword.errors.allFieldsRequired"));
      setLoading(false);
      return;
    }

    const passwordError = validatePassword(newPassword);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("resetPassword.errors.passwordsDoNotMatch"));
      setLoading(false);
      return;
    }

    const response = await resetPasswordWithToken(
      email,
      token,
      newPassword,
      confirmPassword,
    );

    if (response.success) {
      setResetSuccess(true);
      toast.success(t("resetPassword.success"));
    } else {
      setError(response.message);
    }

    setLoading(false);
  };

  if (resetSuccess) {
    return (
      <div className={styles.loginPageWrapper}>
        {/* Animated background shapes */}
        <div className={styles.backgroundShapes}>
          <div className={`${styles.shape} ${styles.shape1}`}></div>
          <div className={`${styles.shape} ${styles.shape2}`}></div>
          <div className={`${styles.shape} ${styles.shape3}`}></div>
          <div className={`${styles.shape} ${styles.shape4}`}></div>
          <div className={`${styles.shape} ${styles.shape5}`}></div>
        </div>

        <Card
          style={{ maxWidth: 520 }}
          sx={{
            p: 4,
            boxShadow: 1,
            width: "100%",
            zIndex: 10,
            position: "relative",
          }}
        >
          <Logo style={{ maxWidth: 70 }} className="mb-3 mx-auto d-block" />

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>✅</div>
            <Typography
              variant="h4"
              sx={{ mb: SPACING.sm, fontWeight: 600, color: "success.main" }}
            >
              {t("resetPassword.successTitle")}
            </Typography>
            <Typography
              variant="bodyMd"
              sx={{ mb: 3, color: "text.secondary" }}
            >
              {t("resetPassword.successMessage")}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Link to="/login" className="text-decoration-none">
              <Button variant="primary" size="lg">
                {t("resetPassword.continueToLogin")}
              </Button>
            </Link>
          </Box>
        </Card>
      </div>
    );
  }

  if (!email || !token) {
    return (
      <div className={styles.loginPageWrapper}>
        {/* Animated background shapes */}
        <div className={styles.backgroundShapes}>
          <div className={`${styles.shape} ${styles.shape1}`}></div>
          <div className={`${styles.shape} ${styles.shape2}`}></div>
          <div className={`${styles.shape} ${styles.shape3}`}></div>
          <div className={`${styles.shape} ${styles.shape4}`}></div>
          <div className={`${styles.shape} ${styles.shape5}`}></div>
        </div>

        <Card
          style={{ maxWidth: 520 }}
          sx={{
            p: 4,
            boxShadow: 1,
            width: "100%",
            zIndex: 10,
            position: "relative",
          }}
        >
          <Logo style={{ maxWidth: 70 }} className="mb-3 mx-auto d-block" />

          <Box sx={{ textAlign: "center", mb: 4 }}>
            <div style={{ fontSize: "48px", marginBottom: "16px" }}>⚠️</div>
            <Typography
              variant="h4"
              sx={{ mb: SPACING.sm, fontWeight: 600, color: "error.main" }}
            >
              {t("resetPassword.invalidTitle")}
            </Typography>
            <Typography
              variant="bodyMd"
              sx={{ mb: 3, color: "text.secondary" }}
            >
              {t("resetPassword.invalidMessage")}
            </Typography>
          </Box>

          <Box sx={{ textAlign: "center" }}>
            <Link to="/forgot-password" className="text-decoration-none">
              <Button variant="primary" size="lg">
                {t("resetPassword.requestNew")}
              </Button>
            </Link>
          </Box>

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Link to="/login" className="text-decoration-none">
              <Typography variant="bodySm" color="primary">
                {t("forgotPassword.backToLogin")}
              </Typography>
            </Link>
          </Box>
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.loginPageWrapper}>
      {/* Animated background shapes */}
      <div className={styles.backgroundShapes}>
        <div className={`${styles.shape} ${styles.shape1}`}></div>
        <div className={`${styles.shape} ${styles.shape2}`}></div>
        <div className={`${styles.shape} ${styles.shape3}`}></div>
        <div className={`${styles.shape} ${styles.shape4}`}></div>
        <div className={`${styles.shape} ${styles.shape5}`}></div>
      </div>

      <Card
        style={{ maxWidth: 520 }}
        sx={{
          p: 4,
          boxShadow: 1,
          width: "100%",
          zIndex: 10,
          position: "relative",
        }}
      >
        <Logo style={{ maxWidth: 70 }} className="mb-3 mx-auto d-block" />

        <Box sx={{ textAlign: "center", mb: 4 }}>
          <div style={{ fontSize: "48px", marginBottom: "16px" }}>🔑</div>
          <Typography variant="h3" sx={{ mb: SPACING.sm, fontWeight: 600 }}>
            {t("resetPassword.title")}
          </Typography>
          <Typography variant="bodyMd" sx={{ color: "text.secondary" }}>
            {t("resetPassword.description")}
          </Typography>
        </Box>

        {error && (
          <div className="mb-3">
            <span className="text-danger small">{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="py-2">
            <TextField
              label={t("resetPassword.newPassword")}
              name="newPassword"
              type={showPassword ? "text" : "password"}
              value={formData.newPassword}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("resetPassword.newPasswordPlaceholder")}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="py-2">
            <TextField
              label={t("resetPassword.confirmPassword")}
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={formData.confirmPassword}
              onChange={handleInputChange}
              variant="outlined"
              fullWidth
              margin="normal"
              placeholder={t("resetPassword.confirmPasswordPlaceholder")}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={20} />
                      ) : (
                        <Eye size={20} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <Box sx={{ mt: SPACING.sm, mb: 3 }}>
            <Typography
              variant="bodySm"
              sx={{ color: "text.secondary", fontSize: "0.875rem" }}
            >
              {t("resetPassword.requirements")}
            </Typography>
            <ul
              style={{
                margin: "8px 0",
                paddingLeft: "20px",
                color: "#666",
                fontSize: "0.875rem",
              }}
            >
              <li>{t("resetPassword.requirement1")}</li>
              <li>{t("resetPassword.requirement2")}</li>
            </ul>
          </Box>

          <div className="py-2 mt-3">
            <Button variant="primary" size="lg" type="submit" loading={loading}>
              {t("resetPassword.resetButton")}
            </Button>
          </div>
        </form>

        {/* Back to login link */}
        <div className="d-flex justify-content-center align-items-center py-4">
          <Typography variant="bodyMd" className="px-1 txt-lighter">
            {t("forgotPassword.rememberPassword")}
          </Typography>
          <Link to="/login" className="px-1 fw-500 text-decoration-none">
            <Typography variant="bodyMd" color="primary">
              {t("forgotPassword.backToLogin")}
            </Typography>
          </Link>
        </div>
      </Card>
    </div>
  );
}
